import React, { FC, useState, useEffect } from "react";
import { Alert, Typography, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import styles from "@/components/forms/inputs/upload.module.css";
import FileTableIcon from "@/components/files/fileTableIcon";
import { Divider, List, ListItem } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import { api } from "@/helper/api";
import { store } from "@/components/state/store";
import FileErrorModal from "@/components/forms/errorHandling/fileErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import classNames from "classnames";
import GenericErrorMessageModal from "@/components/forms/errorHandling/genericErrorMessageModal";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import DropDown from "@/components/forms/inputs/dropDown";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";
import useFileUploadProgress, {
  FileUploadRequestOptions,
  useConcurrentFileUploadProgress,
} from "@/helper/customHooks/useFileUploadProgress";
import FileUploadProgress from "@/components/forms/inputs/fileUploadProgress";

type UploadValidationTarget = "subject" | "folder" | "files" | "filename";

interface Props {
  name?: string;
  label?: string;
  entity?: string;
  onCancel?: any;
  handleUploadSuccess?: (modalOpen: boolean) => void;
  multiple: boolean;
  target_filename?: any;
  subjectId?: any;
  subjectIdsForBatch?: string[];
  folderId?: any;
  companyId?: any;
  evkUpload?: boolean;
  customUploadHandler?: (
    files: File[],
    options: FileUploadRequestOptions,
  ) => Promise<void>;
  accept?: string[];
  documentsForAppointmentUpload?: boolean;
  appointmentId?: any;
  showSubjectSelection?: boolean;
  usePersistedSubjectSearchValue?: boolean;
  handleSuccessCallback?: (data: any) => void;
  showFolderSelection?: boolean;
  onUploadStateChange?: (active: boolean) => void;
}

const FileUploadInput: FC<Props> = ({
  /*eslint-disable */
  name,
  label,
  entity,
  onCancel,
  handleUploadSuccess,
  multiple,
  target_filename,
  subjectId,
  subjectIdsForBatch,
  folderId,
  companyId,
  evkUpload,
  customUploadHandler,
  accept,
  documentsForAppointmentUpload,
  appointmentId,
  showSubjectSelection = false,
  usePersistedSubjectSearchValue = false,
  handleSuccessCallback,
  showFolderSelection = false,
  onUploadStateChange,
}) => {
  // eslint-disable-next-line
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>([]);
  const [filesToUpload, setFilesToUpload] = useState<any>([]);
  const [progressMode, setProgressMode] = useState<"single" | "concurrent">(
    "single",
  );
  //for BatchSubjectUpload

  // eslint-disable-next-line
  const [uploadCount, setUploadcount] = useState<number>(0);
  const [uploadDone, setUploadDone] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [selectedSubject, setSelectedSubject] = useState<any>(null);
  const [editingFileIndex, setEditingFileIndex] = useState<number | null>(null);
  const [customFilenames, setCustomFilenames] = useState<{
    [key: number]: string;
  }>({});
  const [editingFilename, setEditingFilename] = useState<string>("");
  const [preservedFilenameExtension, setPreservedFilenameExtension] =
    useState<string>("");
  const [folderOptions, setFolderOptions] = useState<any[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [showSingleFileWarning, setShowSingleFileWarning] =
    useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState({
    subject: false,
    folder: false,
    files: false,
    filename: false,
  });
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<
      Record<string, never>,
      UploadValidationTarget
    >({ fieldOrder: [] });
  const singleUploadProgress = useFileUploadProgress();
  const concurrentUploadProgress = useConcurrentFileUploadProgress();
  const visibleUploadProgress =
    progressMode === "concurrent"
      ? concurrentUploadProgress
      : singleUploadProgress;

  const { t } = useTranslation();
  const persistedSubjectSearchValue = useSelector(
    (state: RootState) => state.subjectSearch,
  );

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];
  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    fileRejections,
  } = useDropzone({
    maxSize: 40_000_000,
    multiple: true,
    useFsAccessApi: false,
    disabled: uploading,
    onDropAccepted(files) {
      singleUploadProgress.resetUploadProgress();
      concurrentUploadProgress.resetUploadProgress();
      setValidationErrors((current) => ({ ...current, files: false }));
      if (multiple) {
        addFilesToUpload(files);
      } else if (files.length > 0) {
        setFilesToUpload([files[files.length - 1]]);
        setShowSingleFileWarning(files.length > 1);
      }
    },
    accept: accept
      ? accept.reduce((acc: { [key: string]: string[] }, type) => {
          acc[`application/${type.replace(".", "")}`] = [type];
          return acc;
        }, {})
      : {
          "application/msword": [".doc"],
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            [".docx"],
          "image/jpeg": [".jpg", ".jpeg"],
          "image/png": [".png"],
          "application/pdf": [".pdf"],
          "application/vnd.ms-excel": [".xls"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
            ".xlsx",
          ],
          "text/csv": [".csv"],
        },
  });

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  const handleSubjectSelection = (selectedOption: any) => {
    setSelectedSubject(selectedOption);
    setValidationErrors((current) => ({ ...current, subject: false }));
  };

  useEffect(() => {
    if (
      usePersistedSubjectSearchValue &&
      persistedSubjectSearchValue &&
      Object.keys(persistedSubjectSearchValue).length !== 0 &&
      "companyId" in persistedSubjectSearchValue
    ) {
      setSelectedSubject(persistedSubjectSearchValue);
    }
  }, [usePersistedSubjectSearchValue, persistedSubjectSearchValue]);

  useEffect(() => {
    if (!showFolderSelection) {
      setSelectedFolderId("");
      return;
    }

    const getFolders = async () => {
      try {
        const response: any = await new Promise((resolve, reject) => {
          api.genericApiRequest({
            entity: "folder",
            method: "get",
            successHandler: (res: any) => resolve(res),
            parametersToRender: {
              additionalUrlParameters: { tree__name: "default_manage" },
            },
            failHandler: (err: any) => reject(err),
          });
        });

        const options = (response?.data?.results || []).map((folder: any) => ({
          value: folder.id,
          label: folder.name,
        }));
        setFolderOptions(options);
      } catch (err) {
        console.log(err);
      }
    };

    getFolders();
  }, [showFolderSelection]);

  const confirmError = () => {
    setError(false);
  };

  const addFilesToUpload = (files: any) => {
    const filteredItems = files.filter(
      (i: any) => !filesToUpload.some((ix: any) => i.name === ix.name),
    );
    for (const key in filteredItems) {
      const newItems = filteredItems[key];
      setFilesToUpload((prev: any) => [...prev, newItems]);
    }
  };
  const handleUpload = async () => {
    const nextValidationErrors = {
      ...validationErrors,
      subject: showSubjectSelection && !selectedSubject?.value,
      folder: showFolderSelection && !selectedFolderId,
      files: filesToUpload.length === 0,
    };
    setValidationErrors(nextValidationErrors);

    const invalidTargets: UploadValidationTarget[] = [];
    if (nextValidationErrors.subject) invalidTargets.push("subject");
    if (nextValidationErrors.folder) invalidTargets.push("folder");
    if (nextValidationErrors.files) invalidTargets.push("files");
    if (invalidTargets.length > 0) {
      focusFirstInvalidField({}, invalidTargets);
      return;
    }

    try {
      setUploading(true);
      onUploadStateChange?.(true);

      if (customUploadHandler) {
        setProgressMode("single");
        concurrentUploadProgress.resetUploadProgress();
        singleUploadProgress.startUpload();
        await customUploadHandler(filesToUpload, {
          onUploadProgress: singleUploadProgress.handleUploadProgress,
        });
        if (handleUploadSuccess) {
          handleUploadSuccess(true);
        }
      } else if (subjectIdsForBatch && subjectIdsForBatch?.length) {
        try {
          setProgressMode("single");
          concurrentUploadProgress.resetUploadProgress();
          singleUploadProgress.startUpload();

          const formData = new FormData();
          const folderIdToUse = showFolderSelection ? selectedFolderId : folderId;
          if (folderIdToUse) {
            formData.append("folder_id", folderIdToUse);
          }
          formData.append("subject_set", JSON.stringify(subjectIdsForBatch));

          if (showSubjectSelection && selectedSubject?.value) {
            formData.append("impersonate_subject", selectedSubject.value);
          } else if (subjectId) {
            formData.append("impersonate_subject", subjectId);
          }

          filesToUpload.forEach((file: File) => {
            formData.append("file_set", file, file.name);
          });

          const config = {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: singleUploadProgress.handleUploadProgress,
          };
          const uploadResponse: any = await new Promise((resolve, reject) => {
            api.genericApiRequest({
              entity: "file",
              additionalRouteParts: { multi_upload: "/" },
              method: "post",
              successHandler: (response: any) => {
                resolve(response);
              },
              failHandler: (error: any) => {
                reject(error);
              },
              submitData: formData,
              config,
              disableGlobalSpinner: true,
            });
          });
          const taskTrackingId = uploadResponse?.data?.task_tracking_id;
          if (!taskTrackingId) {
            throw new Error("No task_tracking_id returned from multi_upload");
          }

          const pollTask = async (taskId: string) => {
            while (true) {
              const res: any = await new Promise((resolve, reject) => {
                api.genericApiRequest({
                  entity: "taskTracking",
                  entityId: taskId,
                  additionalRouteParts: { wait_for_task_to_complete: "/" },
                  method: "get",
                  disableGlobalSpinner: true,
                  successHandler: (r: any) => resolve(r),
                  failHandler: (err: any) => reject(err),
                });
              });

              const status = res?.data?.status;
              if (status === "completed") return res;
              if (status === "failed")
                throw new Error("Waiting for Task failed");

              await new Promise((r) => setTimeout(r, 5000));
            }
          };

          const finalResponse = await pollTask(taskTrackingId);

          setNotificationMessage(t("upload_successful"));
          setNotificationVariant("success");
          setShowNotification(true);
          handleSuccessCallback?.(finalResponse.data);
          handleUploadSuccess?.(true);
        } catch (error) {
          setNotificationMessage(t("upload_failed"));
          setNotificationVariant("error");
          setShowNotification(true);
          setErrorResponse(error);
          setError(true);
          handleUploadSuccess?.(false);
        }
      } else {
        setProgressMode("concurrent");
        singleUploadProgress.resetUploadProgress();
        concurrentUploadProgress.startUpload(filesToUpload);
        const promises = filesToUpload.map(
          async (file: any, fileIndex: number) => {
            return new Promise((resolve) => {
              const selectedChildSubjectId: any =
                store.getState()?.parent?.subjectId;

              const formData = new FormData();
              formData.append("file", file);
              formData.append("original_filename", file.name);
              const customFilename = customFilenames[fileIndex];
              if (customFilename) {
                formData.append("target_file_name", customFilename);
              } else if (target_filename) {
                formData.append("target_file_name", target_filename);
              }
              if (showSubjectSelection && selectedSubject?.value) {
                formData.append("impersonate_subject", selectedSubject.value);
              } else if (subjectId) {
                formData.append("impersonate_subject", subjectId);
              }
              if (
                selectedChildSubjectId !== "" &&
                !showSubjectSelection &&
                !selectedSubject?.value
              ) {
                formData.append("impersonate_subject", selectedChildSubjectId);
              }
              if (folderId) {
                formData.append("folder_id", folderId);
              }
              if (evkUpload) {
                formData.append("data_collection_agreement", "true");
              }
              if (documentsForAppointmentUpload) {
                formData.append("entity_type", "Appointment");
                formData.append("entity_id", appointmentId);
              }

              const config = {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
                onUploadProgress: (data: any) =>
                  concurrentUploadProgress.handleUploadProgress(
                    fileIndex,
                    data,
                  ),
              };

              api.genericApiRequest({
                entity: entity === "user" ? "user/worker_csv" : "file",
                method: "post",
                successHandler: () => {
                  concurrentUploadProgress.markUploadComplete(fileIndex);
                  setUploadcount((prevstate: number) => prevstate + 1);
                  resolve({ file: file.name, status: true });
                },
                failHandler: (error: any) => {
                  concurrentUploadProgress.markUploadComplete(fileIndex);
                  resolve({
                    file: file.name,
                    status: false,
                    error: error,
                  });
                },
                submitData: formData,
                config: config,
                disableGlobalSpinner: true,
              });
            });
          },
        );

        await Promise.all(promises).then((value: any) => {
          const successfulUpload = [];
          const failedUploads = [];
          for (const object of value) {
            if (object.status === false) {
              failedUploads.push(object);
            } else successfulUpload.push(object);
          }

          if (failedUploads.length !== 0) {
            for (const failedObj of failedUploads) {
              setNotificationMessage(
                t("upload_failed") + " - " + failedObj.file,
              );
              setNotificationVariant("error");
              setShowNotification(true);
            }
            for (const obj of successfulUpload) {
              setUploadDone(true);
              setNotificationMessage(t("upload_successful") + " - " + obj.file);
              setNotificationVariant("success");
              setShowNotification(true);

              if (handleUploadSuccess) {
                handleUploadSuccess(false);
              }
            }
            const combinedErrors = failedUploads.reduce((acc, obj) => {
              const key = obj.file;
              acc[key] = obj.error;
              return acc;
            }, {});
            setErrorResponse(combinedErrors);
            setError(true);
          } else {
            for (const obj of successfulUpload) {
              setUploadDone(true);
              setNotificationMessage(t("upload_successful") + " - " + obj.file);
              setNotificationVariant("success");
              setShowNotification(true);
              if (handleUploadSuccess) {
                handleUploadSuccess(true);
              }
            }
          }
          setUploading(false);
        });
      }
    } catch (error: any) {
      setErrorResponse(error);
      setError(error);
      setNotificationMessage(t("upload_failed"));
      setNotificationVariant("error");
      setShowNotification(true);
    } finally {
      setUploading(false);
      onUploadStateChange?.(false);
      singleUploadProgress.resetUploadProgress();
      concurrentUploadProgress.resetUploadProgress();
    }
  };
  const { handleSubmit: handleUploadSubmit } = useFormSubmission({
    onSubmit: handleUpload,
    disabled: uploading,
  });

  const styleClasses = classNames(styles.dropzoneBase, {
    [styles.dropzoneFocused]: isFocused,
    [styles.dropzoneAccept]: isDragAccept,
    [styles.dropzoneReject]: isDragReject,
  });

  const errorMessage = [];
  if (fileRejections.length != 0) {
    for (const key in fileRejections) {
      const fileItem = fileRejections[key].file;
      const fileName = fileItem.name;
      const errorItem = fileRejections[key].errors[0];
      const errorCode = errorItem.code;
      let message;

      if (errorCode === "file-too-large") {
        message = t("upload_file_size_too_large");
      }

      errorMessage.push(
        <>
          <ListItem className={styles.errorMessageWrapper} key={fileName}>
            <FileTableIcon color="red" />
            <span className={styles.errorMessageStyle}>
              {fileName} : {message}
            </span>
          </ListItem>
          <Divider component={"li"} />
        </>,
      );
    }
  }

  const removeFile = (item: any, i: any) => {
    if (uploading) {
      return;
    }
    const filter = filesToUpload.filter((item: any, index: any) => index !== i);
    setFilesToUpload(filter);
    const newCustomFilenames: { [key: number]: string } = {};
    Object.keys(customFilenames).forEach((key) => {
      const oldIndex = parseInt(key);
      if (oldIndex < i) {
        newCustomFilenames[oldIndex] = customFilenames[oldIndex];
      } else if (oldIndex > i) {
        newCustomFilenames[oldIndex - 1] = customFilenames[oldIndex];
      }
    });
    setCustomFilenames(newCustomFilenames);
    if (editingFileIndex === i) {
      setEditingFileIndex(null);
      setEditingFilename("");
      setPreservedFilenameExtension("");
    } else if (editingFileIndex !== null && editingFileIndex > i) {
      setEditingFileIndex(editingFileIndex - 1);
    }
  };

  const handleStartEdit = (index: number, currentName: string) => {
    const filenameToEdit = customFilenames[index] || currentName;
    const extension = getFileExtension(filenameToEdit);
    const nameWithoutExtension = extension
      ? filenameToEdit.substring(0, filenameToEdit.lastIndexOf("."))
      : filenameToEdit;

    setEditingFileIndex(index);
    setEditingFilename(nameWithoutExtension);
    setPreservedFilenameExtension(extension);
    setValidationErrors((current) => ({ ...current, filename: false }));
  };

  const handleSaveEdit = (index: number) => {
    if (!editingFilename.trim()) {
      setValidationErrors((current) => ({ ...current, filename: true }));
      focusFirstInvalidField({}, ["filename"]);
      return;
    }

    const newFilename = preservedFilenameExtension
      ? `${editingFilename.trim()}.${preservedFilenameExtension}`
      : editingFilename.trim();
    setCustomFilenames({
      ...customFilenames,
      [index]: newFilename,
    });
    setValidationErrors((current) => ({ ...current, filename: false }));
    setEditingFileIndex(null);
    setEditingFilename("");
    setPreservedFilenameExtension("");
  };

  const handleCancelEdit = () => {
    setEditingFileIndex(null);
    setEditingFilename("");
    setPreservedFilenameExtension("");
    setValidationErrors((current) => ({ ...current, filename: false }));
  };

  const getDisplayFilename = (file: any, index: number) => {
    return customFilenames[index] || file.name;
  };

  const getFileExtension = (filename: string): string => {
    const lastDot = filename.lastIndexOf(".");
    if (lastDot === -1 || lastDot === filename.length - 1) {
      return "";
    }
    return filename.substring(lastDot + 1).toLowerCase();
  };

  function formatFilesize(filesize: number) {
    const kiloBytes = 1024;
    const megaBytes = kiloBytes * 1024;

    if (filesize < megaBytes) {
      return (filesize / kiloBytes).toFixed(2) + " KB";
    } else {
      return (filesize / megaBytes).toFixed(2) + " MB";
    }
  }

  const files = filesToUpload.map((file: any, index: any) => {
    const isEditing = editingFileIndex === index;
    const displayFilename = getDisplayFilename(file, index);
    const fileExtension = getFileExtension(file.name);

    return (
      <React.Fragment key={`${file.name}-${index}`}>
        <ListItem className={styles.fileListItem} disablePadding>
          <div className={styles.gridContainer}>
            <FileTableIcon extension={fileExtension} />
            {isEditing ? (
              <>
                <input
                  ref={registerValidationTarget("filename")}
                  type="text"
                  value={editingFilename}
                  onChange={(e) => {
                    setEditingFilename(e.target.value);
                    setValidationErrors((current) => ({
                      ...current,
                      filename: false,
                    }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSaveEdit(index);
                    } else if (e.key === "Escape") {
                      handleCancelEdit();
                    }
                  }}
                  className={styles.filenameInput}
                  aria-invalid={validationErrors.filename}
                  disabled={uploading}
                  autoFocus
                />
                {validationErrors.filename && (
                  <Typography color="error" variant="caption" role="alert">
                    {t("field_required")}
                  </Typography>
                )}
                <div className={styles.fileActions}>
                  <Tooltip
                    arrow
                    disableInteractive
                    title={t("save")}
                    placement="top"
                    enterDelay={2000}
                    enterNextDelay={500}
                  >
                    <GenericButton
                      type="button"
                      variant="icon"
                      color="secondary"
                      onClick={() => handleSaveEdit(index)}
                      className={styles.editActionButton}
                      disabled={uploading}
                    >
                      <CheckIcon fontSize="small" />
                    </GenericButton>
                  </Tooltip>
                  <Tooltip
                    arrow
                    disableInteractive
                    title={t("cancel")}
                    placement="top"
                    enterDelay={2000}
                    enterNextDelay={500}
                  >
                    <GenericButton
                      type="button"
                      variant="icon"
                      color="error"
                      onClick={handleCancelEdit}
                      className={styles.editActionButton}
                      disabled={uploading}
                    >
                      <CancelIcon fontSize="small" />
                    </GenericButton>
                  </Tooltip>
                </div>
              </>
            ) : (
              <>
                <p className={styles.fileListItemName}>
                  {displayFilename} - {formatFilesize(file.size)}
                </p>
                <div className={styles.fileActions}>
                  <Tooltip
                    arrow
                    disableInteractive
                    title={t("rename")}
                    placement="top"
                    enterDelay={2000}
                    enterNextDelay={500}
                  >
                    <GenericButton
                      type="button"
                      variant="icon"
                      color="iconBase"
                      onClick={() => handleStartEdit(index, file.name)}
                      className={styles.editButton}
                      disabled={uploading}
                    >
                      <EditIcon fontSize="small" />
                    </GenericButton>
                  </Tooltip>
                  <Tooltip
                    arrow
                    disableInteractive
                    title={t("remove")}
                    placement="top"
                    enterDelay={2000}
                    enterNextDelay={500}
                  >
                    <GenericButton
                      type="button"
                      variant="icon"
                      color="error"
                      onClick={() => removeFile(file, index)}
                      disabled={uploading}
                    >
                      <CloseIcon />
                    </GenericButton>
                  </Tooltip>
                </div>
              </>
            )}
          </div>
        </ListItem>
        <Divider component={ListItem} disablePadding />
      </React.Fragment>
    );
  });

  return (
    <>
      <form className={styles.uploadWrapper} onSubmit={handleUploadSubmit}>
        {showSubjectSelection && (
          <div
            className={styles.subjectSelectionWrapper}
            ref={registerValidationTarget("subject")}
          >
            <FormFieldLabel
              label={t("select_company")}
              htmlFor="upload-company"
              mandatory
            />
            <DropDownSubjectSearch
              name="upload-company"
              disabled={uploading}
              subdomain={subdomain}
              queryParameter="companies"
              onChange={handleSubjectSelection}
              preExistingValue={
                usePersistedSubjectSearchValue &&
                persistedSubjectSearchValue &&
                Object.keys(persistedSubjectSearchValue).length !== 0 &&
                "companyId" in persistedSubjectSearchValue
                  ? persistedSubjectSearchValue
                  : undefined
              }
            />
            {validationErrors.subject && (
              <Typography color="error" variant="caption" role="alert">
                {t("field_required")}
              </Typography>
            )}
          </div>
        )}
        {showFolderSelection && (
          <div
            className={styles.subjectSelectionWrapper}
            ref={registerValidationTarget("folder")}
          >
            <DropDown
              options={folderOptions}
              useReduxDefaultValue={false}
              label={t("folder")}
              mandatory={true}
              required={true}
              disabled={uploading}
              onChange={(selectedOption: any) => {
                setSelectedFolderId(selectedOption?.value || "");
                setValidationErrors((current) => ({
                  ...current,
                  folder: false,
                }));
              }}
            />
            {validationErrors.folder && (
              <Typography color="error" variant="caption" role="alert">
                {t("field_required")}
              </Typography>
            )}
          </div>
        )}
        <div ref={registerValidationTarget("files")}>
          <div
            {...getRootProps({
              className: styleClasses,
            })}
          >
            <input
              {...getInputProps()}
              multiple={multiple}
              className={styles.hiddenInput}
            />
            <p className={styles.uploadInputText}>
              {multiple
                ? t("upload_description")
                : t("upload_description_single_file")}
            </p>
            <p className={`${styles.uploadInputText} ${styles.noMargin}`}>
              {accept?.length === 1
                ? t("upload_file_type")
                : t("upload_file_types")}
              :{" "}
              {accept
                ? accept.join(", ")
                : ".doc, .docx, .png, .jpg, .jpeg, .xls, .xlsx, .pdf, .csv"}
            </p>
            <p className={`${styles.uploadInputText} ${styles.noMargin}`}>
              {t("max_upload_filesize_40mb")}
            </p>
          </div>
          {validationErrors.files && (
            <Typography color="error" variant="caption" role="alert">
              {t("no_file_selected")}
            </Typography>
          )}
        </div>
        <div className={styles.uploadButtonWrapper}>
          <GenericButton
            variant="outlined"
            onClick={() => {
              if (onCancel) {
                onCancel();
              }
            }}
            disabled={uploading}
          >
            {t("cancel")}
          </GenericButton>

          <GenericButton
            type="submit"
            disabled={uploading}
          >
            {t("upload")}
          </GenericButton>
        </div>
        {errorMessage.length !== 0 && <List>{errorMessage}</List>}
        {filesToUpload.length !== 0 && (
          <aside>
            <h2 className={styles.fileListHeading}>{t("files")}</h2>
            <List className={styles.fileList}>{files}</List>
          </aside>
        )}
        <FileUploadProgress
          phase={visibleUploadProgress.phase}
          progressPercentage={visibleUploadProgress.progressPercentage}
          className={styles.uploadProgress}
        />
        {!multiple && showSingleFileWarning && (
          <Alert
            severity="warning"
            variant="filled"
            onClose={() => setShowSingleFileWarning(false)}
            className={styles.singleFileWarning}
          >
            {t("upload_single_file_only_last_kept")}
          </Alert>
        )}
      </form>
      {error === true && (
        <FileErrorModal
          uploadedFilesValues={errorResponse}
          error={error}
          onErrorConfirm={confirmError}
        />
      )}
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
      {error && error !== true && error !== "" && (
        <GenericErrorMessageModal
          title={t("error_occurred")}
          error={error}
          onClosehandler={() => {
            setError("");
          }}
        />
      )}
    </>
  );
};

export default FileUploadInput;

//TODO: Allow Delete on accepted Files - Delete Icon in List - Index
//TODO: If already files were chosen and accepted, let user add files to accepted files instead of selecting all new
//TODO CUSTOMERROR
